// roles.js
//
// Client-side mirror of the role vocabulary and privilege ranking defined by the
// server in `server/lib/roles.js`. Three roles forming a strict hierarchy:
//
//   superadmin  (rank 2)  everything an administrator can do, plus anything
//                         explicitly reserved to superadmins
//   admin       (rank 1)  manage projects, and manage employee/admin accounts
//   employee    (rank 0)  sign in and use the non-administrative screens
//
// Why this is duplicated rather than shared: the client and server are separate
// npm packages with no shared module, so a small mirrored table is the pragmatic
// option. It is safe to duplicate because it is only ever used for NAVIGATION
// and UI affordances — the server independently enforces every rule on the
// endpoints themselves, and the users API also returns the assignable-role set
// so the UI can follow the server's answer rather than its own copy.
//
// Every helper is pure and total: unknown, absent, or malformed input resolves to
// the LEAST privileged interpretation, so a bad value can never grant access in
// the UI.

export const ROLE_SUPERADMIN = 'superadmin'
export const ROLE_ADMIN = 'admin'
export const ROLE_EMPLOYEE = 'employee'

/** Ordered least- to most-privileged. */
export const ROLES = [ROLE_EMPLOYEE, ROLE_ADMIN, ROLE_SUPERADMIN]

/** Privilege rank. Higher means more access. */
export const RANK = Object.freeze({
  [ROLE_EMPLOYEE]: 0,
  [ROLE_ADMIN]: 1,
  [ROLE_SUPERADMIN]: 2
})

/** Least privilege by default. */
export const DEFAULT_ROLE = ROLE_EMPLOYEE

/** Human-readable labels for display. */
export const ROLE_LABELS = Object.freeze({
  [ROLE_EMPLOYEE]: 'Employee',
  [ROLE_ADMIN]: 'Administrator',
  [ROLE_SUPERADMIN]: 'Super administrator'
})

/**
 * True iff `role` is exactly one of the known role strings. Case-sensitive and
 * exact, so 'Admin' is not valid and cannot be coerced into a privilege level.
 *
 * @param {*} role
 * @returns {boolean}
 */
export function isKnownRole (role) {
  return typeof role === 'string' && ROLES.indexOf(role) !== -1
}

/**
 * Reduce any value to a known role, falling back to the least privileged.
 *
 * @param {*} role
 * @returns {'employee'|'admin'|'superadmin'}
 */
export function normalizeRole (role) {
  return isKnownRole(role) ? role : DEFAULT_ROLE
}

/**
 * Privilege rank of a role. Unknown input ranks lowest.
 *
 * @param {*} role
 * @returns {number}
 */
export function rankOf (role) {
  return RANK[normalizeRole(role)]
}

/**
 * Is `role` at least as privileged as `minimum`?
 *
 * The predicate UI gating should use: `hasAtLeast(x, ROLE_ADMIN)` is true for
 * both admin and superadmin, so the higher tier is never excluded by accident.
 *
 * @param {*} role
 * @param {*} minimum
 * @returns {boolean}
 */
export function hasAtLeast (role, minimum) {
  return rankOf(role) >= rankOf(minimum)
}

/**
 * Resolve a role from a profile or user-shaped object.
 *
 * Prefers the authoritative `role` field. Falls back to the legacy `admin`
 * boolean (or `is_admin`) for payloads written before the role field existed,
 * mapping a legacy administrator to `admin` — never to `superadmin`, so the
 * elevated role is only ever reported when explicitly present.
 *
 * @param {*} source a profile or managed-user object
 * @returns {'employee'|'admin'|'superadmin'}
 */
export function resolveRole (source) {
  if (!source || typeof source !== 'object') return DEFAULT_ROLE
  if (isKnownRole(source.role)) return source.role
  const legacyAdmin = source.admin === true || source.is_admin === true
  return legacyAdmin ? ROLE_ADMIN : ROLE_EMPLOYEE
}

/**
 * Display label for a role.
 *
 * @param {*} role
 * @returns {string}
 */
export function labelForRole (role) {
  return ROLE_LABELS[normalizeRole(role)]
}

/**
 * The roles a given acting role may assign, least privileged first.
 *
 * Mirrors the server's `assignableRoles`. Prefer the `assignableRoles` array the
 * users API returns when it is available; this exists for building a selector
 * before that response has arrived.
 *
 * @param {*} actingRole
 * @returns {string[]}
 */
export function assignableRoles (actingRole) {
  const ceiling = rankOf(actingRole)
  return ROLES.filter((role) => rankOf(role) <= ceiling)
}

export default {
  ROLE_SUPERADMIN,
  ROLE_ADMIN,
  ROLE_EMPLOYEE,
  ROLES,
  RANK,
  DEFAULT_ROLE,
  ROLE_LABELS,
  isKnownRole,
  normalizeRole,
  rankOf,
  hasAtLeast,
  resolveRole,
  labelForRole,
  assignableRoles
}
